import React, { useState, useEffect } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SendIcon from '@mui/icons-material/Send'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import EmailIcon from '@mui/icons-material/Email'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Typography,
  Grid,
  Chip,
  Box,
  FormControl,
  Select,
  SelectChangeEvent,
  MenuItem,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import SuccessModal from '../../../components/SuccessModal'
import LeadComments from '../../Leads/components/LeadComments'
import FormErrorsModal from '../../../components/FormErrorsModal'
import classes from './style.module.scss'
import axiosInstance from '../../../utils/axiosInstance'
import { leadStatuses, USER_ROLES } from '../../../constants'
import { Lead } from '../OrderItem/LeadsByOrder'
import { useDispatch, useSelector } from '../../../store/store'
import LeadEmailHistory, { LeadEmailHistoryEntry } from './LeadEmailHistory'
import NewLeadNotificationButton from './NewLeadNotificationButton'
import { setTrigger } from '../../../store/slices/lead'
import useApplicantPrivacy from '../../../hooks/useApplicantPrivacy'
import { classifyLeadField } from '../../../utils/leadFields'
import AtsPushStatusNote, {
  AtsPushState,
  useAtsPushState,
} from '../../Leads/components/AtsPushStatusNote'

// Types
interface LeadItemProps {
  lead: {
    id: string
    firstName: string
    lastName: string
    email: string
    phone: string
    platform: string
    status?: string
    createdAt: string
    fieldData: any
    emailHistory?: LeadEmailHistoryEntry[]
    applicantCV?: {
      id: string
      name: string
      file_filename: string
      file_filesize: number
      createdAt: string
    } | null
    atsPush?: AtsPushState
  }
  orderJobs?: Array<{
    atsAuthConfig?: {
      type: string
    }
  }>
  setSelectedLead: (lead: Lead) => void
  atsProviders: Record<string, { name: string; capabilities: string[] }>
  open: boolean
  onClose: () => void
  orderId?: string
  refetch?: () => void
  onAtsSuccess?: (responseId?: string) => void
}

// Styled components
const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
}))

const LeadItem: React.FC<LeadItemProps> = ({
  lead,
  orderJobs,
  atsProviders,
  open,
  setSelectedLead,
  onClose,
  orderId,
  refetch,
  onAtsSuccess,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { role } = useSelector((state) => state?.user.userInfo)
  // Mirrors the server's NEW_LEAD_NOTIFICATION_ROLES; the endpoint enforces it too.
  const canNotifyCustomer = [USER_ROLES.ADMIN.value, USER_ROLES.INTERNAL_USER.value].includes(
    role || ''
  )
  const privacy = useApplicantPrivacy()
  const applicant = {
    firstName: lead.firstName,
    lastName: lead.lastName,
    email: lead.email,
    phone: lead.phone,
  }
  const cvRequestCount = (lead.emailHistory || []).filter((e) => e.type === 'askForCV').length

  const [status, setStatus] = useState(lead.status || 'new')
  const [isSendingToAts, setIsSendingToAts] = useState(false)

  // Sync status state with lead prop when it changes
  useEffect(() => {
    setStatus(lead.status || 'new')
  }, [lead.status])
  // Keeps the ATS note current while the ATS confirms a push, without reloading the list.
  const { atsPush, setAtsPush, confirmedNow } = useAtsPushState(
    lead.id,
    lead.atsPush,
    open,
    (fresh) => {
      // A push the ATS rejected puts the lead back to "new" on the server.
      if (fresh.atsPushStatus === 'failed' && fresh.status) setStatus(fresh.status)
    }
  )
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [formikErrors, setFormikErrors] = useState<string[]>([])
  const [errorsModalOpen, setErrorsModalOpen] = useState(false)
  const [isSendingCvRequest, setIsSendingCvRequest] = useState(false)
  const [cvRequestSuccessModalOpen, setCvRequestSuccessModalOpen] = useState(false)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [isDownloadingCv, setIsDownloadingCv] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewContent, setPreviewContent] = useState<{
    html: string
    subject: string
    to: string
  } | null>(null)

  // Parse field data
  const fieldData = typeof lead.fieldData === 'string' ? JSON.parse(lead.fieldData) : lead.fieldData

  // Check if lead has been synced to ATS
  const isLeadSynced = status === 'synced'

  // Get ATS capability information
  const getAtsInfo = () => {
    // Check if already synced
    if (isLeadSynced) {
      return { disabled: true, tooltip: 'This lead has already been synced to the ATS' }
    }

    if (!orderJobs || orderJobs.length === 0) {
      return { disabled: true, tooltip: 'No job associated with this lead' }
    }

    const job = orderJobs[0]
    const atsType = job.atsAuthConfig?.type

    if (!atsType || !atsProviders[atsType]) {
      return { disabled: true, tooltip: 'No ATS configured for this job' }
    }

    const hasPushCapability = atsProviders[atsType].capabilities.includes('applicants:push')

    if (!hasPushCapability) {
      return {
        disabled: true,
        tooltip: `${atsProviders[atsType].name} doesn't have the push capability`,
      }
    }

    return { disabled: false, tooltip: 'Push this lead to the ATS' }
  }

  const atsInfo = getAtsInfo()

  // Update lead status function using server route
  const [updateLoading, setUpdateLoading] = useState(false)
  const [rejectionConfirmOpen, setRejectionConfirmOpen] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<string | null>(null)

  const updateLead = async (data: any, where: any) => {
    try {
      setUpdateLoading(true)
      const response = await axiosInstance.put(`/lead/${where.id}/update`, data)
      return response.data
    } catch (error) {
      console.error('Error updating lead:', error)
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleStatusChange = async (event: SelectChangeEvent) => {
    const newStatus = event.target.value
    if (newStatus === 'rejected') {
      setPendingStatus(newStatus)
      setRejectionConfirmOpen(true)
      return
    }
    await applyStatusUpdate(newStatus, false)
  }

  const applyStatusUpdate = async (newStatus: string, sendRejectionEmail: boolean) => {
    setStatus(newStatus)
    try {
      await axiosInstance.post('/lead/update-status', {
        leadId: lead.id,
        status: newStatus,
        sendRejectionEmail,
        orderId,
      })
      dispatch(setTrigger(true))
      if (refetch) refetch()
      setSuccessModalOpen(true)
      setSelectedLead({ ...lead, status: newStatus })
    } catch (error: any) {
      console.error('Error updating lead status:', error)
      setFormikErrors([error.response?.data?.message || error.message || 'Failed to update status'])
      setErrorsModalOpen(true)
    }
  }

  const handleRejectionConfirm = async (sendEmail: boolean) => {
    setRejectionConfirmOpen(false)
    if (pendingStatus) {
      await applyStatusUpdate(pendingStatus, sendEmail)
      setPendingStatus(null)
    }
  }

  const handleRejectionCancel = () => {
    setRejectionConfirmOpen(false)
    setPendingStatus(null)
  }

  const handleSendToAts = async () => {
    setIsSendingToAts(true)

    try {
      const response = await axiosInstance.post('/send-lead-to-talentbridge', {
        leadId: lead.id,
      })

      // Capture ATS response ID
      const responseId = response.data?.talentBridgeData?.id

      // Update status to synced
      setStatus('synced')
      // The loaded note (waiting / failed) is out of date now; watch the new push instead.
      setAtsPush({ atsPushStatus: 'confirming' })
      await updateLead({ status: 'synced' }, { id: lead.id })

      // Refresh lead list
      dispatch(setTrigger(true))
      if (refetch) refetch()

      // Show success modal in parent with ATS ID (dialog stays open so user sees button disabled)
      if (onAtsSuccess) {
        onAtsSuccess(responseId)
      }
    } catch (error: any) {
      console.error('Error sending to ATS:', error)
      const responseData = error?.response?.data
      const detailMessages = responseData?.details?.message
      const errorMessage =
        error?.response?.status === 409
          ? 'This application has already been submitted to ATS. Duplicate applications are not allowed.'
          : Array.isArray(detailMessages)
          ? detailMessages.join(', ')
          : detailMessages ||
            responseData?.message ||
            responseData?.error ||
            'Failed to send lead to ATS'

      setFormikErrors([errorMessage])
      setErrorsModalOpen(true)
    } finally {
      setIsSendingToAts(false)
    }
  }

  // Get status color class
  const getStatusClass = (status: string): string => {
    return classes[status] || ''
  }

  // The applicant's own name, email and phone are among the form answers too.
  const maskFieldValue = (name: string, value: any) => {
    if (!privacy.isMasked || typeof value !== 'string') return value
    switch (classifyLeadField(name)) {
      case 'full_name':
      case 'first_name':
      case 'last_name':
        return privacy.maskName(value)
      case 'email':
        return privacy.maskEmail(value)
      case 'phone_number':
        return privacy.maskPhone(value)
      default:
        return privacy.maskText(value, applicant)
    }
  }

  // Format field data for display
  const formatFieldData = () => {
    if (!fieldData || (Array.isArray(fieldData) && fieldData.length === 0)) {
      return []
    }

    if (Array.isArray(fieldData)) {
      return fieldData.map((item) => ({
        name: item.name?.replace(/_/g, ' '),
        value: maskFieldValue(
          item.name,
          Array.isArray(item.values) ? item.values.join(', ') : item.values || ''
        ),
      }))
    }

    // Handle object format
    return Object.entries(fieldData).map(([key, value]) => ({
      name: key.replace(/_/g, ' '),
      value: maskFieldValue(key, value),
    }))
  }

  const formattedFields = formatFieldData()

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
    navigate({
      search: '',
    })
  }

  const handleSuccess = () => {
    setSuccessModalOpen(false)
    if (onClose) onClose()
  }

  // Download the CV through the authenticated, ownership-checked endpoint.
  // The private object is streamed via our API — the browser never receives a
  // direct S3 URL, so there is no shareable/leakable link.
  const handleDownloadCv = async () => {
    if (!orderId || !lead.applicantCV) return

    setIsDownloadingCv(true)
    try {
      const response = await axiosInstance.get(`/leads-by-order/${orderId}/leads/${lead.id}/cv`, {
        responseType: 'blob',
      })

      const fileName = lead.applicantCV.name.endsWith('.pdf')
        ? lead.applicantCV.name
        : `${lead.applicantCV.name}.pdf`

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading CV:', error)
    } finally {
      setIsDownloadingCv(false)
    }
  }

  const handleSendCvRequest = async () => {
    setIsSendingCvRequest(true)

    try {
      await axiosInstance.post(`/lead/${lead.id}/send-cv-request`)

      // Refresh lead data to show the new email in emailHistory
      if (refetch) refetch()

      // Show success modal
      setCvRequestSuccessModalOpen(true)
    } catch (error: any) {
      console.error('Error sending CV request:', error)
      const errorMessage =
        error?.response?.data?.message || 'Failed to send CV request email to applicant'
      setFormikErrors([errorMessage])
      setErrorsModalOpen(true)
    } finally {
      setIsSendingCvRequest(false)
    }
  }

  const handleCvRequestSuccess = () => {
    setCvRequestSuccessModalOpen(false)
  }

  const handleRejectionPreview = async () => {
    setIsLoadingPreview(true)

    try {
      const response = await axiosInstance.get(
        `/lead/${lead.id}/rejection-preview${orderId ? `?orderId=${orderId}` : ''}`
      )

      const previewData = response.data
      if (previewData?.html) {
        const logoUrl = `${window.location.origin}/rec-logo.png`

        previewData.html = previewData.html.replace(
          /<div class="recruspin-logo">[\s\S]*?<\/div>/i,
          `<div class="recruspin-logo">
          <img
            src="${logoUrl}"
            alt="recruspin logo"
            width="27"
          />
        </div>`
        )
      }

      setPreviewContent(previewData)
      setPreviewModalOpen(true)
    } catch (error: any) {
      console.error('Error loading rejection email preview:', error)
      const errorMessage = error?.response?.data?.message || 'Failed to load email preview'
      setFormikErrors([errorMessage])
      setErrorsModalOpen(true)
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const handlePreview = async () => {
    setIsLoadingPreview(true)

    try {
      const response = await axiosInstance.get(`/lead/${lead.id}/cv-request-preview`)

      // Fix logo URL in the preview HTML
      const previewData = response.data
      if (previewData?.html) {
        // Replace old logo URLs with new local logo (use absolute URL for iframe)
        const logoUrl = `${window.location.origin}/rec-logo.png`

        // Replace the entire logo div section with our simple version (legacy template)
        previewData.html = previewData.html.replace(
          /<div class="logo">[\s\S]*?<\/div>/i,
          `<div class="logo">
          <img
            src="${logoUrl}"
            alt="Recruspin Logo"
            width="20"
          />
        </div>`
        )

        // Replace footer logo (new CV request template)
        previewData.html = previewData.html.replace(
          /<div class="recruspin-logo">[\s\S]*?<\/div>/i,
          `<div class="recruspin-logo">
          <img
            src="${logoUrl}"
            alt="recruspin logo"
            width="27"
          />
        </div>`
        )
      }

      setPreviewContent(previewData)
      setPreviewModalOpen(true)
    } catch (error: any) {
      console.error('Error loading email preview:', error)
      const errorMessage = error?.response?.data?.message || 'Failed to load email preview'
      setFormikErrors([errorMessage])
      setErrorsModalOpen(true)
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const handleClosePreview = () => {
    setPreviewModalOpen(false)
    setPreviewContent(null)
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        className={classes.leadDialog}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant="h3">
            {privacy.maskName(lead.firstName)} {privacy.maskName(lead.lastName)}
          </Typography>
          <CloseButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="body2" color="textSecondary">
                  Created: {new Date(lead.createdAt).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item>
                <Chip
                  label={lead.status}
                  className={`${classes.statusChip} ${getStatusClass(lead.status || 'new')}`}
                />
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box className={classes.infoSection}>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
                {lead.email && (
                  <Typography variant="body1" className={classes.infoItem}>
                    <span className={classes.fieldLabel}>Email:</span>{' '}
                    {privacy.maskEmail(lead.email)}
                  </Typography>
                )}
                {lead.phone && (
                  <Typography variant="body1" className={classes.infoItem}>
                    <span className={classes.fieldLabel}>Phone:</span>{' '}
                    {privacy.maskPhone(lead.phone)}
                  </Typography>
                )}
                {lead.platform && (
                  <Typography variant="body1" className={classes.infoItem}>
                    <span className={classes.fieldLabel}>Platform:</span> {lead.platform}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* CV Section */}
            <Grid item xs={12} md={6}>
              <Box className={classes.infoSection}>
                <Typography variant="h6" gutterBottom>
                  Applicant CV
                </Typography>
                {lead.applicantCV ? (
                  <Box>
                    <Typography variant="body1" className={classes.infoItem}>
                      <span className={classes.fieldLabel}>File:</span>{' '}
                      {privacy.maskText(lead.applicantCV.name, applicant)}
                    </Typography>
                    <Typography variant="body1" className={classes.infoItem}>
                      <span className={classes.fieldLabel}>Uploaded:</span>{' '}
                      {new Date(lead.applicantCV.createdAt).toLocaleString()}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadCv}
                        disabled={isDownloadingCv}
                      >
                        {isDownloadingCv ? 'Downloading...' : 'Download CV'}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No CV uploaded yet
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box className={classes.fieldDataSection}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Additional Information
                </Typography>
                <Box className={classes.additionalFields}>
                  <Grid container spacing={2}>
                    {formattedFields.length > 0 ? (
                      formattedFields.map((field, index) => (
                        <Grid item xs={12} sm={6} key={index} className={classes.fieldRow}>
                          <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                            <span className={classes.fieldLabel}>{field.name}:</span> {field.value}
                          </Typography>
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="body1" color="textSecondary">
                          No additional information available
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box className={classes.actionsSection}>
                <Typography variant="h6" gutterBottom>
                  Actions
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Typography variant="body2" gutterBottom>
                      Status
                    </Typography>
                    <Select
                      defaultValue={lead.status}
                      onChange={handleStatusChange}
                      displayEmpty
                      variant="outlined"
                      size="small"
                      disabled={updateLoading}
                    >
                      {leadStatuses.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {process.env.REACT_APP_ENV !== 'production' && orderId && (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        Dev
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        href={`${window.location.origin}/application?o=${orderId}&l=${lead.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open Application Page
                      </Button>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" gutterBottom>
                      ATS Integration
                    </Typography>
                    <Tooltip title={atsInfo.tooltip}>
                      <span>
                        <Button
                          variant="contained"
                          color={isLeadSynced ? 'success' : 'secondary'}
                          startIcon={isLeadSynced ? <CheckCircleIcon /> : <SendIcon />}
                          onClick={handleSendToAts}
                          disabled={isSendingToAts || atsInfo.disabled || isLeadSynced}
                        >
                          {isLeadSynced ? 'Synced' : 'Push to ATS'}
                        </Button>
                      </span>
                    </Tooltip>
                    <AtsPushStatusNote {...atsPush} confirmedNow={confirmedNow} />
                  </Box>

                  {canNotifyCustomer && orderId && (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        Customer notification
                      </Typography>
                      <NewLeadNotificationButton leadId={lead.id} onSent={refetch} />
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Manual Email Send Section */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Send Email Manually</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Send a CV request email to the applicant. This will use the customer&apos;s
                      email settings to generate and send the email.
                    </Typography>

                    {lead.email ? (
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Email will be sent to: <strong>{privacy.maskEmail(lead.email)}</strong>
                        </Alert>

                        {cvRequestCount > 0 && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            Note: CV request email has already been sent to this applicant (
                            {cvRequestCount} time
                            {cvRequestCount > 1 ? 's' : ''})
                          </Alert>
                        )}

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handlePreview}
                            disabled={isLoadingPreview || isSendingCvRequest}
                          >
                            {isLoadingPreview ? 'Loading...' : 'Preview Email'}
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<EmailIcon />}
                            onClick={handleSendCvRequest}
                            disabled={isSendingCvRequest || isLoadingPreview}
                          >
                            {isSendingCvRequest ? 'Sending...' : 'Ask for Applicant CV'}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        No email address found for this lead. Cannot send CV request.
                      </Alert>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12}>
              <LeadEmailHistory
                leadId={lead.id}
                emails={lead.emailHistory || []}
                applicant={applicant}
              />
            </Grid>

            {/* Comments Section */}
            <Grid item xs={12}>
              <LeadComments leadId={lead.id} />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <SuccessModal
        successModalOpen={successModalOpen}
        handleClose={handleSuccess}
        setSuccessModalOpen={setSuccessModalOpen}
        message="Lead updated successfully"
      />

      <SuccessModal
        successModalOpen={cvRequestSuccessModalOpen}
        handleClose={handleCvRequestSuccess}
        setSuccessModalOpen={setCvRequestSuccessModalOpen}
        message="CV request email sent to applicant successfully"
      />

      <FormErrorsModal
        errorsModalOpen={errorsModalOpen}
        formikErrors={formikErrors}
        setErrorsModalOpen={setErrorsModalOpen}
      />

      {/* Rejection confirmation dialog */}
      <Dialog open={rejectionConfirmOpen} onClose={handleRejectionCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Rejection</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reject this applicant? A rejection <strong>mail</strong> will
            be sent to the applicant.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectionCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleRejectionPreview}
            color="secondary"
            variant="outlined"
            disabled={isLoadingPreview}
          >
            {isLoadingPreview ? 'Loading...' : 'Preview Email'}
          </Button>
          <Button onClick={() => handleRejectionConfirm(false)} color="error" variant="outlined">
            Reject without email
          </Button>
          <Button onClick={() => handleRejectionConfirm(true)} color="error" variant="contained">
            Reject &amp; send mail
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Preview Modal */}
      <Dialog open={previewModalOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Email Preview</Typography>
          <CloseButton aria-label="close" onClick={handleClosePreview}>
            <CloseIcon />
          </CloseButton>
        </DialogTitle>
        <DialogContent dividers>
          {previewContent && (
            <>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>To:</strong> {privacy.maskText(previewContent.to, applicant)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Subject:</strong> {privacy.maskText(previewContent.subject, applicant)}
                </Typography>
              </Box>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  minHeight: '400px',
                  overflow: 'auto',
                  bgcolor: 'white',
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                  },
                }}
              >
                <iframe
                  sandbox=""
                  srcDoc={privacy.maskHtml(previewContent.html, applicant)}
                  style={{
                    width: '100%',
                    minHeight: '500px',
                    border: 'none',
                  }}
                  title="Email Preview"
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default LeadItem
